import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Copy,
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Filter,
  ShoppingCart,
  Lock,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { CAS, CHAPTERS, STATS, filterCas, pickFreeCaseIds } from "@/data/casPratiques";
import { KDP_URL, KDP_AVAILABLE } from "@/lib/kdpConfig";
import { useAuth } from "@/contexts/AuthContext";
import { useBilling } from "@/contexts/BillingContext";
import PromptCopiedModal from "@/components/PromptCopiedModal";
import PlaceholderResolverModal, { extractPlaceholders } from "@/components/PlaceholderResolverModal";

// Number of prompts kept fully visible (SEO + demo) for non-Pro users.
// The remaining prompts are locked until the first credit-pack purchase.
// Shared with Dashboard's FreeCasesStrip via pickFreeCaseIds in data/casPratiques.js.

/**
 * /enseignants/cas-pratiques
 * ------------------------------------------------------------------
 * Discovery page listing the 50 book use cases:
 *   - 20 testable in-app (click → prefills Dashboard with sample text,
 *     format and audience level, then user hits "Résumer")
 *   - 30 copyable prompts (click → clipboard)
 *
 * Design is intentionally content-dense, mirroring the editorial feel
 * of /enseignants (SUX1 blue + red accent, mono uppercase labels).
 */
export default function CasPratiques() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { billing, startCheckout } = useBilling();
  const isPro = !!billing?.has_paid;
  const [chapter, setChapter] = useState("all");
  const [type, setType] = useState("all");
  // Modal state: when a user clicks "Copier le prompt" on a prompt-type
  // card, we show PromptCopiedModal so they know what to do next.
  const [copiedCas, setCopiedCas] = useState(null);
  // Set when the clicked cas pratique contains [BRACKETED] placeholders —
  // we open the PlaceholderResolverModal first so the user can fill them
  // before anything hits the clipboard.
  const [placeholderCas, setPlaceholderCas] = useState(null);

  const filtered = useMemo(() => filterCas({ chapter, type }), [chapter, type]);
  // Which case IDs are available to free users today (stable rotation across
  // ALL 50 cases). Pro users bypass the gate entirely.
  const freePromptIds = useMemo(() => pickFreeCaseIds(), []);
  const freePromptCount = freePromptIds.size;
  const lockedPromptCount = STATS.total - freePromptCount;

  const handleUpgrade = async () => {
    if (!user) {
      // Non-authed visitors must sign up first — redirect with a return
      // path so they land back on this page after login.
      navigate("/signup", { state: { next: "/enseignants/cas-pratiques" } });
      return;
    }
    try {
      await startCheckout();
    } catch (e) {
      toast.error("Impossible d'ouvrir le paiement. Réessayez dans quelques secondes.");
    }
  };

  // Testable → navigate to Dashboard with router state (same mechanism as
  // "Retravailler ce texte" from SummaryDetail). Dashboard reads
  // location.state.rework on mount and prefills text + format + model +
  // audience level, then wipes the state.
  const handleTest = (c) => {
    // Hand off everything via router state so the Dashboard applies
    // the full configuration (text + format + model + audience) on
    // mount, regardless of whether it's already instantiated. The
    // localStorage mirror below is a belt-and-braces fallback for the
    // (rare) case where the router state is dropped during auth
    // redirects.
    try {
      if (c.audienceLevel) {
        localStorage.setItem("sux1_audience_level", c.audienceLevel);
      }
    } catch (_) {}
    navigate("/", {
      state: {
        rework: {
          text: c.sampleText,
          format: c.format,
          model: c.model,
          audienceLevel: c.audienceLevel,
          sourceTitle: `Cas pratique #${c.id} — ${c.title}`,
        },
      },
    });
  };

  const handleCopy = (c) => {
    // If the prompt has [BRACKETED] placeholders, intercept the copy and
    // ask the user to fill them first — otherwise the clipboard would hold
    // a half-baked prompt that still contains "[NIVEAU]" or "[TYPE]".
    if (extractPlaceholders(c.prompt).length > 0) {
      setPlaceholderCas(c);
      return;
    }
    // Copy silently to the clipboard (fire-and-forget — modal is the
    // real feedback). If the Clipboard API fails (older browsers), we
    // still show the modal so the user can at least open SUX1 or
    // ChatGPT; worst case they'll copy manually from the preview.
    try {
      navigator.clipboard.writeText(c.prompt).catch(() => {});
    } catch (_) {}
    setCopiedCas(c);
  };

  // Called by PlaceholderResolverModal when the user has filled every input
  // and clicked "Copier le prompt personnalisé". `resolvedPrompt` already
  // has the brackets substituted.
  //
  // Branching logic (Oct 2026 — Famille A vs Famille B):
  //   • Famille B (self_contained=true): the prompt is a complete standalone
  //     request after placeholder substitution — e.g. "Scénarise un escape
  //     game sur [THEME] pour [NIVEAU]". It has no "input text" to transform.
  //     We put the resolved prompt directly in the Dashboard textarea so
  //     the user can click RÉSUMER immediately. `directExecution=true` tells
  //     the backend to execute the prompt literally instead of summarizing it.
  //   • Famille A (default): the prompt is a transformation that expects the
  //     teacher's own content as input ("Génère 10 QCM à partir de ce texte").
  //     We stage it via PromptCopiedModal → "Tester dans SUX1" so the user
  //     lands on the Dashboard with the explainer banner telling them to
  //     paste their content first.
  const handlePlaceholderResolved = (resolvedPrompt) => {
    if (!placeholderCas) return;
    const c = placeholderCas;
    setPlaceholderCas(null);
    if (c.self_contained) {
      // 1-click execution — jump straight to Dashboard with the prompt in
      // the textarea; the user still has a chance to tweak before clicking
      // RÉSUMER.
      navigate("/", {
        state: {
          rework: {
            text: resolvedPrompt,
            directExecution: true,
            sourceTitle: `cas pratique #${c.id} — ${c.title}`,
          },
        },
      });
      return;
    }
    // Famille A — keep the staged copy flow.
    const resolvedCas = { ...c, prompt: resolvedPrompt };
    setCopiedCas(resolvedCas);
  };

  // From the PromptCopiedModal: user chose "Tester dans SUX1".
  //
  // Prompt-type cas pratiques are TRANSFORMATION meta-prompts (QCM, mail
  // parents, DYS, plan de séance, correction-type…). They need the
  // teacher's OWN content as input — not the prompt itself. So instead
  // of pasting the prompt in the main textarea, we drop it into the
  // Dashboard's "Instruction personnalisée" panel (upstream direct-action
  // flow): the teacher just pastes/scans their content and clicks Résumer
  // once — 1 click, 1 quota consumed, final transformation produced in a
  // single shot (no follow-up step needed).
  const handleTestCopiedInSux1 = () => {
    if (!copiedCas) return;
    const c = copiedCas;
    setCopiedCas(null);
    if (c.self_contained) {
      // Famille B — standalone prompt. Put it in the textarea so RÉSUMER
      // activates immediately and the user can tweak it inline.
      navigate("/", {
        state: {
          rework: {
            text: c.prompt,
            directExecution: true,
            sourceTitle: `cas pratique #${c.id} — ${c.title}`,
          },
        },
      });
      return;
    }
    // Famille A — transformation that needs user content. Stage via
    // customInstruction so the explainer banner tells the user to paste
    // their content first.
    navigate("/", {
      state: {
        rework: {
          customInstruction: c.prompt,
          sourceTitle: `cas pratique #${c.id} — ${c.title}`,
        },
      },
    });
  };

  // From the PromptCopiedModal: user chose "Tester dans ChatGPT".
  // Prompt is already on the clipboard — just open chat.openai.com
  // in a new tab.
  const handleTestCopiedInChatGPT = () => {
    setCopiedCas(null);
    window.open("https://chat.openai.com/", "_blank", "noopener,noreferrer");
  };

  // Does this specific case require a Pro unlock for the current viewer?
  // Gating now applies to ALL 50 cases (testable + prompt) so the ratio
  // stays a clean 5 libres / 45 verrouillés.
  const isPromptLocked = (c) => {
    if (isPro) return false; // Pro bypass
    return !freePromptIds.has(c.id);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ======== HERO ======== */}
      <section className="border-b-2 border-[#002FA7]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-6">
            <Link
              to="/enseignants"
              data-testid="back-to-enseignants"
              className="font-mono uppercase tracking-[0.14em] text-[11px] text-muted-foreground hover:text-foreground"
            >
              ← /enseignants
            </Link>
          </div>
          <div className="sux-marker mb-4" style={{ color: "#FF2A00" }}>
            Les 50 cas pratiques du livre
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 max-w-4xl">
            50 prompts pédagogiques,
            <br />
            testés en classe,
            <br />
            <span style={{ color: "#002FA7" }}>copiables en 1 clic.</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mb-10">
            Chaque cas est un prompt réel issu du livre « L'IA au service des
            enseignants ». <strong>{STATS.testable} cas sont testables
            directement dans l'app</strong> (le texte source est pré-rempli,
            vous appuyez sur « Résumer » et l'IA répond avec le format
            pédagogique adapté). {STATS.prompt} autres sont des prompts prêts à
            copier-coller dans votre outil IA habituel.
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-xl">
            <div className="border-l-2 border-[#002FA7] pl-4">
              <div className="text-3xl md:text-4xl font-bold">
                {STATS.total}
              </div>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mt-1">
                Cas pratiques
              </div>
            </div>
            <div className="border-l-2 pl-4" style={{ borderColor: "#002FA7" }}>
              <div className="text-3xl md:text-4xl font-bold" style={{ color: "#002FA7" }}>
                {STATS.testable}
              </div>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mt-1">
                Testables dans l'app
              </div>
            </div>
            <div className="border-l-2 pl-4" style={{ borderColor: "#FF2A00" }}>
              <div className="text-3xl md:text-4xl font-bold" style={{ color: "#FF2A00" }}>
                {STATS.prompt}
              </div>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mt-1">
                Prompts à copier
              </div>
            </div>
          </div>

          {/* Access banner — tells visitors exactly what's free vs Pro */}
          {!isPro && (
            <div className="mt-10 border-l-2 border-[#002FA7] pl-4 py-2 max-w-2xl">
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-1">
                Accès
              </div>
              <div className="text-sm leading-relaxed">
                <strong>{freePromptCount} prompts libres</strong> (rotation
                quotidienne) · <strong>{lockedPromptCount} prompts réservés
                aux acheteurs</strong>{" "}
                <button
                  type="button"
                  onClick={handleUpgrade}
                  data-testid="hero-upgrade-cta"
                  className="underline hover:no-underline font-medium"
                  style={{ color: "#002FA7" }}
                >
                  Packs SUX1 — dès 2 €
                </button>
                .
              </div>
              <div className="mt-2 text-[13px] leading-relaxed" style={{ color: "#002FA7" }}>
                <strong>Dès 2 € = 50 cas pratiques débloqués + 20 crédits de résumé sans expiration.</strong>{" "}
                <span className="text-muted-foreground">Pas d'abonnement. Paiement sécurisé Stripe.</span>
              </div>
            </div>
          )}
          {isPro && (
            <div className="mt-10 border-l-2 pl-4 py-2 max-w-2xl" style={{ borderColor: "#002FA7" }}>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] mb-1" style={{ color: "#002FA7" }}>
                Accès Pro
              </div>
              <div className="text-sm leading-relaxed">
                <CheckCircle2 className="inline-block w-4 h-4 mr-1" style={{ color: "#002FA7" }} />
                Tous les prompts débloqués. Merci de votre soutien.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ======== FILTERS ======== */}
      <section className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground">
              <Filter className="w-3.5 h-3.5" />
              Filtrer
            </div>

            {/* Type filter */}
            <div className="inline-flex border border-border">
              {[
                { k: "all", label: `Tous (${STATS.total})` },
                { k: "testable", label: `Testables (${STATS.testable})` },
                { k: "prompt", label: `Prompts (${STATS.prompt})` },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  data-testid={`filter-type-${o.k}`}
                  onClick={() => setType(o.k)}
                  className={`font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2 border-r border-border last:border-r-0 transition-colors ${
                    type === o.k
                      ? "bg-[#002FA7] text-white"
                      : "bg-background hover:bg-card"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {/* Chapter filter */}
            <div className="inline-flex flex-wrap border border-border">
              <button
                type="button"
                data-testid="filter-chapter-all"
                onClick={() => setChapter("all")}
                className={`font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2 border-r border-border transition-colors ${
                  chapter === "all"
                    ? "bg-[#002FA7] text-white"
                    : "bg-background hover:bg-card"
                }`}
              >
                Tous les chapitres
              </button>
              {CHAPTERS.map((ch, idx) => (
                <button
                  key={ch.id}
                  type="button"
                  data-testid={`filter-chapter-${ch.id}`}
                  onClick={() => setChapter(ch.id)}
                  className={`font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2 ${
                    idx < CHAPTERS.length - 1 ? "border-r border-border" : ""
                  } transition-colors ${
                    chapter === ch.id
                      ? "bg-[#002FA7] text-white"
                      : "bg-background hover:bg-card"
                  }`}
                >
                  {ch.short}
                </button>
              ))}
            </div>

            <div className="ml-auto font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground">
              {filtered.length} cas affiché{filtered.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </section>

      {/* ======== GRID ======== */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        {filtered.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            Aucun cas ne correspond à ces filtres.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <CasCard
              key={c.id}
              cas={c}
              locked={isPromptLocked(c)}
              onTest={() => handleTest(c)}
              onCopy={() => handleCopy(c)}
              onUpgrade={handleUpgrade}
            />
          ))}
        </div>
      </section>

      {/* ======== WHY SUX1 vs CHATGPT ======== */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="sux-marker mb-4" style={{ color: "#002FA7" }}>
            Pourquoi tester dans SUX1 ?
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 max-w-3xl">
            Copier un prompt dans ChatGPT, c'est bien.
            <br />
            <span style={{ color: "#002FA7" }}>
              Le faire dans SUX1, c'est 10× plus rapide.
            </span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mb-10">
            Un prompt copié dans ChatGPT exige encore de coller le texte
            source à la main (chapitre de manuel, copie d'élève, exercice).
            SUX1 fait tout en un seul upload — PDF, image, EPUB, URL —
            puis enchaîne les 6 actions pédagogiques en 1 clic.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
            {/* ChatGPT column */}
            <div className="border border-border bg-background p-6">
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-3">
                Avec ChatGPT (gratuit)
              </div>
              <h3 className="text-xl font-bold mb-5">
                Copier-coller, étape par étape
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-5">1.</span>
                  <span>Ouvrir ChatGPT, se connecter avec compte OpenAI</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-5">2.</span>
                  <span>Retaper ou photographier le chapitre, puis le coller</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-5">3.</span>
                  <span>Coller le prompt copié</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-5">4.</span>
                  <span>Attendre la réponse, scroller, parfois recommencer</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-5">5.</span>
                  <span>Pour chaque action (QCM, mail, DYS…) : <strong>recommencer</strong></span>
                </li>
              </ul>
              <div className="mt-6 pt-4 border-t border-border font-mono uppercase tracking-[0.12em] text-[10px] text-muted-foreground">
                Temps moyen : <strong className="text-foreground">5-7 min/action</strong>
              </div>
            </div>

            {/* SUX1 column */}
            <div className="border-2 bg-background p-6" style={{ borderColor: "#002FA7" }}>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] mb-3" style={{ color: "#002FA7" }}>
                Avec SUX1 (dès 2 € les 20 résumés)
              </div>
              <h3 className="text-xl font-bold mb-5">
                Un upload. Six actions. Deux clics chacune.
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] shrink-0 w-5" style={{ color: "#002FA7" }}>1.</span>
                  <span>Uploader le PDF / photo / EPUB / URL du chapitre</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] shrink-0 w-5" style={{ color: "#002FA7" }}>2.</span>
                  <span>Obtenir le résumé en streaming (5 secondes)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] shrink-0 w-5" style={{ color: "#002FA7" }}>3.</span>
                  <span>Cliquer un des <strong>6 boutons</strong> : QCM, mail parents, DYS, plan séance, correction, débat</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] shrink-0 w-5" style={{ color: "#002FA7" }}>4.</span>
                  <span>Enchaîner autant d'actions que besoin <strong>pour seulement 1 crédit les 5 actions</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-[11px] shrink-0 w-5" style={{ color: "#002FA7" }}>5.</span>
                  <span>Historique conservé, export PDF, écoute vocale offline</span>
                </li>
              </ul>
              <div className="mt-6 pt-4 border-t font-mono uppercase tracking-[0.12em] text-[10px]" style={{ borderColor: "#002FA7", color: "#002FA7" }}>
                Temps moyen : <strong>30-45 s/action</strong>
              </div>
            </div>
          </div>

          {/* Advantages row */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
            {[
              { label: "Pas de compte OpenAI", value: "Email + mot de passe" },
              { label: "Fichiers acceptés", value: "PDF, EPUB, image, URL" },
              { label: "Prix", value: "Dès 2 € les 20 résumés, sans abonnement" },
              { label: "Hors ligne", value: "App Android + PWA" },
            ].map((item, i) => (
              <div key={i} className="border-l-2 pl-3" style={{ borderColor: "#FF2A00" }}>
                <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-1">
                  {item.label}
                </div>
                <div className="text-sm font-bold">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/"
              data-testid="why-sux1-cta"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-6 py-3 bg-[#002FA7] text-white hover:bg-[#FF2A00] transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Voir les exemples gratuits
            </Link>
            <span className="ml-4 font-mono text-[11px] text-muted-foreground">
              Sans carte bancaire. Packs dès 2 € les 20 résumés quand vous êtes convaincu(e).
            </span>
          </div>
        </div>
      </section>

      {/* ======== BOTTOM CTA ======== */}
      <section className="border-t-2 border-[#002FA7] bg-[#002FA7] text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="sux-marker mb-4" style={{ color: "#FF2A00" }}>
                Le livre complet
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                126 pages. 50 cas détaillés.
                <br />
                14,99 € broché · 9,99 € Kindle.
              </h2>
              <p className="text-white/80 mb-8 max-w-md">
                Chaque cas du livre contient : le problème, le prompt exact, le
                résultat obtenu, le temps gagné, les pièges à éviter.
                Imprimé et livré par Amazon.
              </p>
              {KDP_AVAILABLE && (
                <a
                  href={KDP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-buy-book"
                  className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-6 py-3 bg-[#FF2A00] text-white hover:bg-white hover:text-[#002FA7] transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Acheter le livre sur Amazon
                </a>
              )}
            </div>
            <div>
              <div className="sux-marker mb-4" style={{ color: "#FFFFFF" }}>
                L'app compagnon
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                50 cas débloqués.
                <br />
                20 résumés dès 2 €.
                <br />
                Sans abonnement.
              </h2>
              <p className="text-white/80 mb-8 max-w-md">
                Un premier pack dès 2 € = tous les cas pratiques accessibles
                + 20 crédits de résumé dans l'app. Pas d'abonnement. Exemples
                offerts pour tester avant d'acheter. Compatible ordinateur,
                iPhone, Android.
              </p>
              <Link
                to="/"
                data-testid="cta-try-app"
                className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-6 py-3 bg-white text-[#002FA7] hover:bg-[#FF2A00] hover:text-white transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Lancer un résumé gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Modal: shown after a successful "Copier le prompt" to explain
          what to do next (test in SUX1 or in ChatGPT). */}
      <PromptCopiedModal
        open={!!copiedCas}
        cas={copiedCas}
        isAuthenticated={!!user}
        onClose={() => setCopiedCas(null)}
        onTestInSux1={handleTestCopiedInSux1}
        onTestInChatGPT={handleTestCopiedInChatGPT}
      />

      {/* Modal: shown instead of PromptCopiedModal when the clicked prompt
          contains [BRACKETED] placeholders. Asks the user to fill them
          first, then forwards to PromptCopiedModal with the resolved
          prompt already in the clipboard. */}
      {placeholderCas && (
        <PlaceholderResolverModal
          cas={placeholderCas}
          onCancel={() => setPlaceholderCas(null)}
          onResolved={handlePlaceholderResolved}
        />
      )}
    </div>
  );
}

/** Single case card — compact, filterable. Locked prompts show a Pro CTA. */
function CasCard({ cas, locked = false, onTest, onCopy, onUpgrade }) {
  const chapterMeta = CHAPTERS.find((c) => c.id === cas.chapter);
  const isTestable = cas.type === "testable";
  const isOneClick = cas.self_contained === true;
  return (
    <div
      className="border border-border bg-card p-5 flex flex-col"
      data-testid={`cas-card-${cas.id}`}
    >
      {/* Header: chapter badge + "Aujourd'hui" ribbon (unlocked only) +
          case number */}
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`font-mono uppercase tracking-[0.12em] text-[9px] px-2 py-1 border ${
              locked
                ? "border-muted-foreground text-muted-foreground"
                : isTestable
                  ? "border-[#002FA7] text-[#002FA7]"
                  : "border-[#FF2A00] text-[#FF2A00]"
            }`}
          >
            {chapterMeta?.short}
          </span>
          {!locked && (
            <span className="font-mono uppercase tracking-[0.12em] text-[9px] px-1.5 py-1 bg-[#FF2A00] text-white">
              Aujourd'hui
            </span>
          )}
          {!locked && isOneClick && (
            <span
              data-testid={`cas-oneclick-badge-${cas.id}`}
              className="sux-pulse-ring font-mono uppercase tracking-[0.12em] text-[9px] px-1.5 py-1 bg-[#002FA7] text-white inline-flex items-center gap-1"
              title="Prompt autonome · pas besoin de coller un texte"
            >
              <Zap className="w-2.5 h-2.5" />
              1 clic
            </span>
          )}
        </div>
        <span className="font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1 shrink-0">
          {locked && <Lock className="w-3 h-3" />}
          #{String(cas.id).padStart(2, "0")}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-bold leading-snug mb-2">{cas.title}</h3>

      {/* Teaser */}
      <p className="text-sm text-muted-foreground leading-snug mb-5 flex-1">
        {cas.teaser}
      </p>

      {/* CTA — locked state wins over testable/prompt so the 5/45 ratio
          applies uniformly across all 50 cases. */}
      {locked ? (
        <button
          type="button"
          onClick={onUpgrade}
          data-testid={`cas-unlock-${cas.id}`}
          className="inline-flex items-center justify-between gap-2 w-full font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2.5 bg-[#FF2A00] text-white hover:bg-[#002FA7] transition-colors"
          title="Débloquer tous les cas — pack dès 2 € les 20 résumés"
        >
          <span className="inline-flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" />
            Débloquer — dès 2 €
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : isTestable ? (
        <button
          type="button"
          onClick={onTest}
          data-testid={`cas-test-${cas.id}`}
          className="inline-flex items-center justify-between gap-2 w-full font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2.5 bg-[#002FA7] text-white hover:bg-[#FF2A00] transition-colors"
        >
          <span className="inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            {cas.cta || "Tester dans l'app"}
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onCopy}
          data-testid={`cas-copy-${cas.id}`}
          className="inline-flex items-center justify-between gap-2 w-full font-mono uppercase tracking-[0.14em] text-[10px] px-3 py-2.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
        >
          <span className="inline-flex items-center gap-2">
            <Copy className="w-3.5 h-3.5" />
            Copier le prompt
          </span>
          <CheckCircle2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
